import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  LayoutDashboard, MapPin, Home, ClipboardCheck, ListTodo, 
  Heart, Wheat, Users, BookOpen, BarChart3, Settings, LogOut,
  Crown, User, CreditCard, Search, ShoppingBag, HelpCircle, Sparkles, Store, Bug, Scan, DollarSign, Scale, Target, Package, Globe, MessageCircle, Gift, Scissors, MessageSquare, Shield, Box, GitCompareArrows, Lightbulb, Star, ExternalLink, Image as ImageIcon, Calendar
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Sidebar = ({ companyProfile, onLogout, isAdmin, isDemo, hasFeature, appVersion, hiddenMenus = [], onNavigate, freePlatform }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [suggestionCount, setSuggestionCount] = useState(0);
  const [bugReportCount, setBugReportCount] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);
  const [swarmCount, setSwarmCount] = useState(0);

  // Fetch pending suggestions count for admin
  useEffect(() => {
    const fetchSuggestionCount = async () => {
      if (!isAdmin) return;
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };
        const response = await axios.get(`${API}/api/suggestions/count`, { headers });
        setSuggestionCount(response.data?.count || 0);
      } catch (error) {
      }
    };
    fetchSuggestionCount();
    const interval = setInterval(fetchSuggestionCount, 60000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  // Fetch new bug reports count for admin
  useEffect(() => {
    const fetchBugCount = async () => {
      if (!isAdmin) return;
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/api/bug-reports/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBugReportCount(res.data?.count || 0);
      } catch { /* ignore */ }
    };
    fetchBugCount();
    const interval = setInterval(fetchBugCount, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, [isAdmin]);

  // Fetch chat unread count
  useEffect(() => {
    const fetchChatUnread = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/api/chat/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setChatUnread(res.data?.count || 0);
      } catch { /* ignore */ }
    };
    fetchChatUnread();
    const interval = setInterval(fetchChatUnread, 10000);
    return () => clearInterval(interval);
  }, []);

  // Fetch available swarm count
  useEffect(() => {
    const fetchSwarmCount = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/api/swarm-collections/available`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSwarmCount(Array.isArray(res.data) ? res.data.length : 0);
      } catch { /* ignore */ }
    };
    fetchSwarmCount();
    const interval = setInterval(fetchSwarmCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const mainNavItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
    { icon: Users, label: 'Crew', path: '/?view=crew', feature: 'crew_management' },
    { icon: MessageSquare, label: 'Team Chat', path: '/?view=team-chat', badge: chatUnread, feature: 'team_chat' },
    { icon: MapPin, label: 'Apiaries', path: '/?view=apiaries' },
    { icon: Home, label: 'Hives', path: '/?view=hives' },
    { icon: ClipboardCheck, label: 'Inspections', path: '/?view=inspections' },
    { icon: ListTodo, label: 'Tasks', path: '/?view=tasks' },
    { icon: Heart, label: 'Health', path: '/?view=health' },
  ];

  const operationsNavItems = [
    { icon: Package, label: 'Bulk Add Hives', path: '/?view=bulk-add-hives', feature: 'split_wizard' },
    { icon: Scissors, label: 'Bulk Split', path: '/?view=bulk-split', feature: 'split_wizard' },
    { icon: Scale, label: 'Record Weights', path: '/?view=bulk-weights' },
    { icon: Wheat, label: 'Record Harvests', path: '/?view=harvests', feature: 'harvest_logging' },
    { icon: DollarSign, label: 'Record Sales', path: '/?view=sales', feature: 'record_sales' },
    { icon: Bug, label: 'Mite Control', path: '/?view=treatments' },
    { icon: Crown, label: 'Queen Rearing', path: '/?view=queen-rearing', feature: 'queen_rearing' },
    { icon: Target, label: 'Swarms', path: '/?view=swarm-traps', feature: 'swarm_traps' },
    { icon: Shield, label: 'Rescues', path: '/?view=swarm-collections', badge: swarmCount },
    { icon: BarChart3, label: 'Analytics', path: '/?view=analytics' },
    { icon: GitCompareArrows, label: 'Compare Hives', path: '/?view=hive-comparison' },
  ];

  const configNavItems = [
    { icon: Box, label: 'My Hive Types', path: '/?view=my-hive-types' },
    { icon: ShoppingBag, label: 'Products', path: '/?view=products', feature: 'products' },
    { icon: Settings, label: 'Harvest', path: '/?view=harvest-settings', feature: 'harvest_logging' },
    { icon: Scale, label: 'Weight', path: '/?view=weight-config' },
    { icon: Calendar, label: 'Seasons', path: '/?view=season-settings' },
    { icon: DollarSign, label: 'Hive Costs', path: '/?view=hive-costs', feature: 'hive_costs' },
    { icon: Shield, label: 'Swarm Rescue', path: '/?view=swarm-collection-settings' },
  ];

  const communityNavItems = [
    { icon: Search, label: 'Find Beekeepers', path: '/?view=find-beekeepers' },
    { icon: Globe, label: 'Find Local Honey', path: '/?view=find-honey' },
    { icon: Users, label: 'Find Bee Club', path: '/?view=find-bee-club' },
    { icon: MessageCircle, label: 'Hive Mind', path: '/?view=hive-mind', feature: 'hive_mind' },
    { icon: BookOpen, label: 'Knowledge', path: '/?view=knowledge', feature: 'knowledge_library' },
    { icon: ShoppingBag, label: 'Marketplace', path: '/?view=marketplace', feature: 'marketplace' },
    { icon: Store, label: 'Merch Store', path: '/?view=merch-store', menuKey: 'merch_store' },
    { icon: Lightbulb, label: 'DIY Ideas', path: '/?view=diy-ideas', feature: 'diy_ideas_view' },
    { icon: User, label: 'Builder Profile', path: '/?view=builder-settings' },
    { icon: BarChart3, label: 'Advertise', path: '/?view=advertise' },
  ];

  const accountNavItems = [
    { icon: User, label: 'My Profile', path: '/?view=my-profile', hideInDemo: true },
    { icon: DollarSign, label: 'Pollinate & Prosper', path: '/referral-rewards', hideInDemo: true, hidden: true },
    { icon: CreditCard, label: 'Plans', path: '/?view=subscription', hideInDemo: true, hidden: true },
    { icon: Heart, label: 'Support Thrive', path: '/?view=donations' },
    { icon: HelpCircle, label: 'Help', path: '/?view=help' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/' && !location.search;
    if (path.startsWith('/?view=')) {
      const view = new URLSearchParams(path.split('?')[1]).get('view');
      const currentView = new URLSearchParams(location.search).get('view');
      return currentView === view;
    }
    return location.pathname.startsWith(path.split('?')[0]);
  };

  const handleNavClick = (item) => {
    if (item.action === 'find') {
      window.dispatchEvent(new CustomEvent('openBeekeeperMap'));
      onNavigate?.();
      return;
    }
    if (item.newTab) {
      const baseUrl = window.location.origin;
      window.open(`${baseUrl}${item.path}?_=${Date.now()}`, '_blank', 'noopener');
      onNavigate?.();
      return;
    }
    navigate(item.path);
    onNavigate?.();
  };

  const NavButton = ({ item, showLock = false }) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    const locked = showLock && item.feature && !hasFeature?.(item.feature);
    
    if (item.hideInDemo && isDemo) return null;
    if (item.hidden) return null;
    if (item.menuKey && hiddenMenus.includes(item.menuKey) && !isAdmin) return null;
    
    return (
      <button
        onClick={() => locked ? navigate('/subscription') : handleNavClick(item)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
          active 
            ? 'bg-amber-100 text-amber-900 font-semibold' 
            : locked
              ? 'text-stone-400'
              : 'text-stone-700 hover:bg-stone-100'
        }`}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        aria-label={item.badge > 0 ? `${item.label} (${item.badge})` : item.label}
      >
        <Icon className={`h-5 w-5 ${active ? 'text-amber-700' : ''}`} />
        <span className="text-sm flex-1">{item.label}</span>
        {item.newTab && <ExternalLink className="h-3 w-3 text-stone-400" />}
        {locked && <span className="text-xs">🔒</span>}
        {item.badge > 0 && (
          <span className="ml-1 bg-amber-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5" aria-hidden="true">
            {item.badge}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="w-56 bg-stone-50 border-r border-stone-200 flex flex-col h-screen sticky top-0">
      {/* Sidebar Header — Thrive Branding */}
      <div className="h-20 flex items-center justify-center border-b border-stone-200 px-4">
        <div className="cursor-pointer" onClick={() => { onNavigate?.(); navigate('/'); }}>
          <img src="/thrive-logo.png" alt="Thrive Beekeeping Management" className="h-14 object-contain" />
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {/* Main Navigation */}
        {mainNavItems.map((item) => (
          <NavButton key={item.path + item.label} item={item} showLock={!!item.feature} />
        ))}
        
        {/* Operations Section */}
        <div className="border-t-2 border-amber-200 my-3 pt-3">
          <p className="text-xs text-amber-600 font-semibold px-3 mb-2 uppercase tracking-wide">Operations</p>
        </div>
        {operationsNavItems.map((item) => (
          <NavButton key={item.path + item.label} item={item} showLock={!!item.feature} />
        ))}
        
        {/* Configurations Section */}
        <div className="border-t-2 border-stone-300 my-3 pt-3">
          <p className="text-xs text-stone-500 font-semibold px-3 mb-2 uppercase tracking-wide">Configurations</p>
        </div>
        {configNavItems.map((item) => (
          <NavButton key={item.path + item.label} item={item} showLock={!!item.feature} />
        ))}
        
        {/* Community Section */}
        <div className="border-t-2 border-teal-200 my-3 pt-3">
          <p className="text-xs text-teal-600 font-semibold px-3 mb-2 uppercase tracking-wide">Community</p>
        </div>
        {communityNavItems.map((item) => (
          <NavButton key={item.path + item.label} item={item} showLock={!!item.feature} />
        ))}
        
        {/* Account Section */}
        <div className="border-t-2 border-stone-300 my-3 pt-3">
          <p className="text-xs text-stone-500 font-semibold px-3 mb-2 uppercase tracking-wide">Account</p>
        </div>
        {accountNavItems.map((item) => (
          <NavButton key={item.path} item={item} />
        ))}
        
        {/* Admin Section */}
        {isAdmin && (
          <>
            <div className="border-t-2 border-purple-200 my-3 pt-3">
              <p className="text-xs text-purple-600 font-semibold px-3 mb-2 uppercase tracking-wide">Admin</p>
            </div>
            <button
              onClick={() => { onNavigate?.(); navigate('/?view=admin-users'); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                location.pathname === '/users'
                  ? 'bg-purple-100 text-purple-900 font-semibold' 
                  : 'text-stone-700 hover:bg-stone-100'
              }`}
              data-testid="nav-users"
            >
              <Users className="h-5 w-5" />
              <span className="text-sm">Users Management</span>
            </button>
            <button
              onClick={() => { onNavigate?.(); navigate('/?view=admin-listings'); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                location.pathname === '/admin/listings'
                  ? 'bg-purple-100 text-purple-900 font-semibold' 
                  : 'text-stone-700 hover:bg-stone-100'
              }`}
              data-testid="nav-marketplace-admin"
            >
              <Store className="h-5 w-5" />
              <span className="text-sm">Marketplace Admin</span>
            </button>
            <button
              onClick={() => { onNavigate?.(); navigate('/?view=admin-plans'); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                location.pathname === '/admin/subscription-plans'
                  ? 'bg-purple-100 text-purple-900 font-semibold' 
                  : 'text-stone-700 hover:bg-stone-100'
              }`}
              data-testid="nav-plan-management"
            >
              <CreditCard className="h-5 w-5" />
              <span className="text-sm">Plan Management</span>
            </button>
            <button
              onClick={() => { onNavigate?.(); navigate('/?view=admin-settings'); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                location.pathname === '/admin/settings'
                  ? 'bg-purple-100 text-purple-900 font-semibold' 
                  : 'text-stone-700 hover:bg-stone-100'
              }`}
              data-testid="nav-app-settings"
            >
              <Settings className="h-5 w-5" />
              <span className="text-sm">App Settings</span>
            </button>
            {/* Referral Program — hidden per user request (Pollinate & Prosper paused) */}
            {/*<button
              onClick={() => { onNavigate?.(); navigate('/?view=admin-referral'); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                location.pathname === '/admin/referral'
                  ? 'bg-purple-100 text-purple-900 font-semibold' 
                  : 'text-stone-700 hover:bg-stone-100'
              }`}
              data-testid="nav-referral-admin"
            >
              <Gift className="h-5 w-5" />
              <span className="text-sm">Referral Program</span>
            </button>*/}
            <button
              onClick={() => { onNavigate?.(); navigate('/?view=admin-config'); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                location.pathname === '/admin/configuration'
                  ? 'bg-purple-100 text-purple-900 font-semibold' 
                  : 'text-stone-700 hover:bg-stone-100'
              }`}
              data-testid="nav-admin-config"
            >
              <Settings className="h-5 w-5" />
              <span className="text-sm">Configuration</span>
            </button>
            <button
              onClick={() => { onNavigate?.(); navigate('/?view=admin-bugs'); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                location.pathname === '/admin/bug-reports'
                  ? 'bg-red-100 text-red-900 font-semibold' 
                  : 'text-stone-700 hover:bg-stone-100'
              }`}
              data-testid="nav-bug-reports"
            >
              <Bug className="h-5 w-5 text-red-500" />
              <span className="text-sm">Bug Reports</span>
              {bugReportCount > 0 && (
                <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5 animate-pulse">
                  {bugReportCount}
                </span>
              )}
            </button>
            <button
              onClick={() => { onNavigate?.(); navigate('/?view=admin-legal'); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                location.pathname === '/admin/legal'
                  ? 'bg-amber-100 text-amber-900 font-semibold' 
                  : 'text-stone-700 hover:bg-stone-100'
              }`}
              data-testid="nav-legal-editor"
            >
              <Shield className="h-5 w-5 text-amber-500" />
              <span className="text-sm">T&C / Privacy</span>
            </button>
            <button
              onClick={() => { onNavigate?.(); navigate('/?view=admin-reviews'); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                location.pathname === '/admin/reviews'
                  ? 'bg-amber-100 text-amber-900 font-semibold' 
                  : 'text-stone-700 hover:bg-stone-100'
              }`}
              data-testid="nav-review-moderation"
            >
              <Star className="h-5 w-5 text-amber-500" />
              <span className="text-sm">Reviews</span>
            </button>
            <button
              onClick={() => { onNavigate?.(); navigate('/?view=admin-honey-requests'); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                location.search.includes('view=admin-honey-requests')
                  ? 'bg-amber-100 text-amber-900 font-semibold' 
                  : 'text-stone-700 hover:bg-stone-100'
              }`}
              data-testid="nav-honey-requests"
            >
              <Globe className="h-5 w-5 text-green-500" />
              <span className="text-sm">Honey Requests</span>
            </button>
            <button
              onClick={() => { onNavigate?.(); navigate('/?view=admin-tours'); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                location.search.includes('view=admin-tours')
                  ? 'bg-amber-100 text-amber-900 font-semibold' 
                  : 'text-stone-700 hover:bg-stone-100'
              }`}
              data-testid="nav-guided-tours"
            >
              <Sparkles className="h-5 w-5 text-amber-500" />
              <span className="text-sm">Guided Tours</span>
            </button>
            <button
              onClick={() => { onNavigate?.(); navigate('/?view=admin-ads'); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                location.pathname === '/admin/ads'
                  ? 'bg-amber-100 text-amber-900 font-semibold' 
                  : 'text-stone-700 hover:bg-stone-100'
              }`}
              data-testid="nav-ad-management"
            >
              <ImageIcon className="h-5 w-5 text-amber-500" />
              <span className="text-sm">Ads</span>
            </button>
            <button
              onClick={() => { onNavigate?.(); navigate('/?view=admin-bee-clubs'); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                location.pathname === '/admin/bee-clubs'
                  ? 'bg-purple-100 text-purple-900 font-semibold' 
                  : 'text-stone-700 hover:bg-stone-100'
              }`}
              data-testid="nav-bee-club-admin"
            >
              <Users className="h-5 w-5 text-purple-500" />
              <span className="text-sm">Bee Clubs</span>
            </button>
            <button
              onClick={() => { onNavigate?.(); navigate('/?view=admin-swarm-reports'); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                location.search.includes('view=admin-swarm-reports')
                  ? 'bg-amber-100 text-amber-900 font-semibold' 
                  : 'text-stone-700 hover:bg-stone-100'
              }`}
              data-testid="nav-swarm-admin"
            >
              <Shield className="h-5 w-5 text-amber-500" />
              <span className="text-sm">Swarm Reports</span>
            </button>
          </>
        )}
      </nav>

      {/* Logout + Version */}
      <div className="p-3 border-t border-stone-200">
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-stone-600 hover:bg-stone-100 transition-all"
          data-testid="sidebar-logout"
        >
          <LogOut className="h-5 w-5" />
          <span className="text-sm">Logout</span>
        </button>
        {appVersion && (
          <p className="text-[10px] text-stone-400 text-center mt-2 select-all" data-testid="app-version">
            v{appVersion.version} • {appVersion.environment}
          </p>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
